import { useEffect } from 'react';
import { updateParkResults, updatePhotospotResults, updatePoiResults, updateTrailResults } from '@alltrails/maps/redux/reducer';
import pinZoomParkAndPOIMaxCount from '@alltrails/maps/utils/pinZoomParkAndPOIMaxCount';
import type { AreaSearchResult, PhotospotSearchResult, POISearchResult, TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import { useAppDispatch, useAppSelector } from '@/utils/redux';

type Args = {
  prioritizedPoiType?: POISearchResult['subtype']; // If provided, all POIs of that type will be stored in redux, beyond the maxPOIs limit
  trailFilter?: (result: TrailSearchResult) => boolean;
};

const useUpdateMapResults = ({ prioritizedPoiType, trailFilter }: Args = {}) => {
  const dispatch = useAppDispatch();
  const { mapZoomBucket, nearbyTrailResults, searchResults, searchIsLoading, skipQuery } = useAppSelector(state => ({
    mapZoomBucket: state.map.mapZoomBucket,
    nearbyTrailResults: state.map.nearbyTrailResults,
    searchResults: state.exploreService.searchResults,
    searchIsLoading: state.exploreService.searchIsLoading,
    skipQuery: state.exploreService.skipQuery
  }));
  const { maxNationalParks, maxStateParks, maxPOIs } = pinZoomParkAndPOIMaxCount(mapZoomBucket ?? 0);

  useEffect(() => {
    const trailResults: TrailSearchResult[] = [];
    const poiResults: POISearchResult[] = [];
    const photospotResults: PhotospotSearchResult[] = [];
    const parkResults: AreaSearchResult[] = [];

    // If the explore service queries are being skipped, we don't want to trigger updates just because the zoom level changes
    if (searchResults && !searchIsLoading && !skipQuery) {
      searchResults?.forEach(result => {
        if (result.type === 'trail') {
          if (!trailFilter || trailFilter(result)) {
            trailResults.push(result);
          }
        } else if (result.type === 'poi') {
          poiResults.push(result);
        } else if (result.type === 'photospot') {
          photospotResults.push(result);
        } else if (result.type === 'area') {
          if (result.area_type === 'N') {
            const nCount = parkResults.filter(park => park.area_type === 'N').length;
            if (maxNationalParks !== undefined && nCount < maxNationalParks) {
              parkResults.push(result);
            } else if (maxNationalParks === undefined) {
              // Do not limit the number of national parks
              parkResults.push(result);
            }
          } else if (result.area_type === 'S') {
            const sCount = parkResults.filter(park => park.area_type === 'S').length;
            if (maxStateParks !== undefined && sCount < maxStateParks) {
              parkResults.push(result);
            } else if (maxStateParks === undefined) {
              // Do not limit the number of state parks
              parkResults.push(result);
            }
          }
        }
      });

      // The "Nearby trails" overlay publishes here when a page opts into the standard pin treatment.
      // Appending them to the same result set (rather than letting the overlay draw its own pins) is what
      // makes nearby and saved pins one system; the page's own results win on a tie.
      if (nearbyTrailResults?.length) {
        const includedIds = new Set(trailResults.map(result => result.ID));
        nearbyTrailResults.forEach(result => {
          if (!includedIds.has(result.ID)) {
            includedIds.add(result.ID);
            trailResults.push(result);
          }
        });
      }

      dispatch(updateTrailResults(trailResults));
      dispatch(updatePhotospotResults(photospotResults));
      dispatch(updateParkResults(parkResults));

      // If the user is so far zoomed out that we would normally show no POIs, don't even show the prioritized POIs
      if (prioritizedPoiType && maxPOIs > 0) {
        // Keep the normal POIs as well, in order, but ensure that all POIs of the prioritized type are included
        const prioritizedPoiResults = poiResults.filter((poi, index) => index < maxPOIs || poi.subtype === prioritizedPoiType);
        dispatch(updatePoiResults(prioritizedPoiResults));
      } else {
        dispatch(updatePoiResults(poiResults.slice(0, maxPOIs)));
      }
    }
  }, [
    dispatch,
    nearbyTrailResults,
    searchIsLoading,
    searchResults,
    skipQuery,
    maxNationalParks,
    maxStateParks,
    maxPOIs,
    trailFilter,
    prioritizedPoiType
  ]);
};

export default useUpdateMapResults;
